import { IcoChevronDown16 } from '@onefootprint/icons';
import type { ApiOnboardingGraph, NewVersionAction, PlaybookDeploymentResponse, PlaybookVersionNoGraph } from '@onefootprint/request-types/dashboard';
import { Button, Dialog, Dropdown, InlineAlert, Tooltip, useConfirmationDialog } from '@onefootprint/ui';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useConfirmClose } from '@/components/playbook-builder';
import { graphOutputs, versionOutputs } from '@/playbook-builder/utils/outputs';
import { requiredPrerequisiteIds, versionPrerequisiteIds } from '@/playbook-builder/utils/prerequisites';
import BreakingOutputsAlert from '../breaking-outputs-alert/breaking-outputs-alert';
import NewPrerequisitesAlert from '../new-prerequisites-alert/new-prerequisites-alert';
import VersionForm, { type VersionFormHandle } from './version-form';
type ActionsDropdownProps = {
  activeDeployment?: PlaybookDeploymentResponse;
  disableErrors: boolean;
  hasErrors: boolean;
  /** Errors on untouched new nodes, hidden until a save attempt; saving then reveals them instead. */
  hasHiddenErrors: boolean;
  onRevealHiddenErrors: () => void;
  hasPendingChanges: boolean;
  hasPermissions: boolean;
  isViewOnly: boolean;
  isDeployed: boolean;
  /** The uncommitted draft that "Save and deploy" is about to turn into a version. */
  graph: ApiOnboardingGraph;
  playbookVersions: PlaybookVersionNoGraph[];
  playbook: {
    canPublish: boolean;
    isImmutable: boolean;
    isEditable: boolean;
    hasProdOnboarding: boolean;
  };
  onReset: () => void;
  onDeploy: (versionId?: string) => void;
  onViewDeployment: () => void;
  onSubmit: (payload: {
    action: NewVersionAction;
    versionDescription?: string;
    skipValidation?: boolean;
  }) => Promise<{
    versionId?: string;
  }>;
  onSubmitAndDeploy: (payload: {
    action: NewVersionAction;
    versionDescription?: string;
    skipValidation?: boolean;
  }) => Promise<{
    versionId?: string;
  }>;
};
const PublishActions = ({
  activeDeployment,
  disableErrors,
  hasErrors,
  hasHiddenErrors,
  onRevealHiddenErrors,
  hasPermissions,
  isViewOnly,
  isDeployed,
  graph,
  playbookVersions,
  onReset,
  onDeploy,
  onViewDeployment,
  onSubmit,
  onSubmitAndDeploy,
  hasPendingChanges,
  playbook
}: ActionsDropdownProps) => {
  const {
    t
  } = useTranslation('playbook-builder');
  const confirm = useConfirmationDialog();
  const versionFormRef = useRef<VersionFormHandle>(null);
  const disabledState = (() => {
    const hasDataErrors = hasErrors && !disableErrors && !hasHiddenErrors;
    const hasPermission = hasPermissions;
    const isEditable = playbook.isEditable;
    const isImmutable = playbook.isImmutable;
    const getTooltipText = () => {
      if (!hasPermission) {
        return t($ => $.publishActions.tooltips.noPermission);
      }
      if (isImmutable || !isEditable) {
        return t($_0 => $_0.publishActions.tooltips.immutableOrNotEditable);
      }
      if (!hasPendingChanges) {
        return t($_1 => $_1.publishActions.tooltips.noPendingChanges);
      }
      if (hasDataErrors) {
        return t($_2 => $_2.publishActions.tooltips.dataErrors);
      }
      return null;
    };
    return {
      value: !hasPermission || !hasPendingChanges || hasDataErrors || isImmutable || !isEditable,
      tooltipText: getTooltipText()
    };
  })();
  const {
    attemptClose: attemptStopEdit
  } = useConfirmClose({
    isDirty: hasPendingChanges,
    onCloseParent: onReset,
    title: t($_3 => $_3.view.confirmBeforeBack.title),
    description: t($_4 => $_4.view.confirmBeforeBack.description)
  });

  // A dialog opened from the menu loses an `autoFocus` to the closing menu, so focus the description here.
  const focusDescription = (event: Event) => {
    event.preventDefault();
    versionFormRef.current?.focus();
  };
  const handleSaveAsDraft = () => {
    if (hasHiddenErrors) return onRevealHiddenErrors();
    confirm.open({
      title: t($_5 => $_5.versionDialog.title),
      onOpenAutoFocus: focusDescription,
      primaryButton: {
        label: t($_6 => $_6.versionDialog.save),
        type: 'submit',
        form: 'version-confirmation-form'
      },
      secondaryButton: {
        label: t($_7 => $_7.versionDialog.cancel)
      },
      children: <VersionForm ref={versionFormRef} action="draft" isProduction={playbook.hasProdOnboarding} graphHasErrors={hasErrors} onSubmit={async ({
        versionDescription,
        skipValidation
      }) => {
        try {
          confirm.startProcessing();
          await onSubmit({
            action: 'draft',
            versionDescription,
            skipValidation
          });
          confirm.hide();
        } finally {
          confirm.stopProcessing();
        }
      }} />
    });
  };
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [deployLoading, setDeployLoading] = useState(false);

  // Publishing replaces the whole deployment, so every currently deployed version is the baseline.
  const deployedVersionIds = (activeDeployment?.routingPolicy.versions ?? []).map(v => v.versionId);
  const deployedVersions = playbookVersions.filter(v_0 => deployedVersionIds.includes(v_0.id));
  const nextVersionNumber = (() => {
    if (playbookVersions.length === 0) return 1;
    return Math.max(...playbookVersions.map(v_1 => v_1.version)) + 1;
  })();
  const handleSaveAndDeploy = () => {
    if (hasHiddenErrors) return onRevealHiddenErrors();
    setDeployDialogOpen(true);
  };
  const handleDeploy = () => onDeploy();
  const renderActions = () => {
    if (isDeployed && !hasPendingChanges) {
      return <Button onClick={onViewDeployment} size="compact" variant="secondary">
          {t($_8 => $_8.publishActions.viewDeployment)}
        </Button>;
    }
    if (!hasPendingChanges) {
      const canDeploy = playbook.canPublish && hasPermissions && !isViewOnly;
      return <div className="flex items-stretch">
          <Button className="!rounded-r-none !border-r-0" disabled={!canDeploy} onClick={handleDeploy} size="compact">
            {t($_9 => $_9.publishActions.deployVersion)}
          </Button>
          <div className={`w-px self-stretch ${canDeploy ? 'bg-[#7e68e1]' : 'bg-tertiary'}`} />
          <Dropdown.Root>
            <Dropdown.Trigger ariaLabel={t($_10 => $_10.publishActions.deployActionsAriaLabel)} disabled={!canDeploy} asChild>
              <Button type="button" disabled={!canDeploy} size="compact" className="!rounded-l-none !border-l-0 !px-1">
                <IcoChevronDown16 color="quinary" />
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end" className="z-drawer">
                <Dropdown.Item onSelect={onViewDeployment}>{t($_11 => $_11.publishActions.viewDeployment)}</Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>;
    }
    return <div className="flex gap-2 items-center">
        {hasPendingChanges && <div className="hidden text-caption-2 text-tertiary min-w-0 truncate md:block">
            {t($_12 => $_12.publishActions.pendingChanges)}
          </div>}
        <div className="flex items-stretch">
          <Tooltip align="center" content={disabledState.tooltipText} disabled={!disabledState.tooltipText} side="left">
            <Button className="!rounded-r-none !border-r-0" disabled={disabledState.value} onClick={handleSaveAsDraft} size="compact" variant="primary">
              {t($_13 => $_13.publishActions.saveAsDraft)}
            </Button>
          </Tooltip>
          <div className="w-px bg-[#7e68e1] self-stretch" />
          <Dropdown.Root>
            <Dropdown.Trigger disabled={disabledState.value} ariaLabel={t($_14 => $_14.publishActions.saveActionsAriaLabel)} asChild>
              <Button type="button" disabled={disabledState.value} variant="primary" size="compact" className="!rounded-l-none !border-l-0">
                <IcoChevronDown16 color="quinary" />
              </Button>
            </Dropdown.Trigger>
            <Dropdown.Portal>
              <Dropdown.Content align="end" className="z-drawer">
                <Dropdown.Item onSelect={handleSaveAsDraft} shortcutHint={['Control', 's']}>
                  {t($_15 => $_15.publishActions.saveAsDraft)}
                </Dropdown.Item>
                <Dropdown.Item onSelect={handleSaveAndDeploy}>{t($_16 => $_16.publishActions.saveAndDeploy)}</Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onSelect={attemptStopEdit.handler} destructive>
                  {t($_17 => $_17.publishActions.discardChanges)}
                </Dropdown.Item>
              </Dropdown.Content>
            </Dropdown.Portal>
          </Dropdown.Root>
        </div>
      </div>;
  };
  return <>
      {renderActions()}
      <Dialog open={deployDialogOpen} onClose={() => setDeployDialogOpen(false)} title={t($_18 => $_18.versionDialog.title)} size="default" isConfirmation onOpenAutoFocus={focusDescription} primaryButton={{
      label: t($_19 => $_19.versionDialog.deploy),
      type: 'submit',
      form: 'version-confirmation-form',
      loading: deployLoading
    }} secondaryButton={{
      label: t($_20 => $_20.versionDialog.cancel),
      onClick: () => setDeployDialogOpen(false)
    }}>
        <div className="flex flex-col gap-6">
          {/* Every alert describes the same deploy, so they read as one block rather than sections. */}
          <div className="flex flex-col gap-3">
            <NewPrerequisitesAlert deployed={versionPrerequisiteIds(deployedVersions)} pending={requiredPrerequisiteIds(graph)} />
            <BreakingOutputsAlert deployed={versionOutputs(deployedVersions)} pending={graphOutputs(graph)} />
            <InlineAlert variant="warning">
              <Trans ns="playbook-builder" i18nKey={$_21 => $_21.versionDialog.deployAlertWithVersion} values={{
              version: nextVersionNumber
            }} components={{
              bold: <strong />
            }} />
            </InlineAlert>
          </div>
          {activeDeployment && <div className="flex flex-col gap-3">
              <h4 className="text-label-2 text-primary">{t($_22 => $_22.versionDialog.currentDeployment)}</h4>
              <div className="flex flex-col gap-2">
                {activeDeployment.routingPolicy.versions.map(v_2 => {
              const pbVersion = playbookVersions.find(pv => pv.id === v_2.versionId);
              return <div key={v_2.versionId} className="flex items-center gap-3 p-3 rounded-lg bg-secondary">
                      <span className="text-label-2 text-primary bg-primary border border-tertiary rounded px-2 py-0.5 shrink-0">
                        v{pbVersion?.version ?? '?'}
                      </span>
                      <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
                        <div className="h-full rounded-full bg-accent" style={{
                    width: `${v_2.weight}%`
                  }} />
                      </div>
                      <span className="text-label-2 text-primary w-12 text-right shrink-0">{v_2.weight}%</span>
                    </div>;
            })}
              </div>
            </div>}
          <VersionForm ref={versionFormRef} action="deploy" hideAlert isProduction={playbook.hasProdOnboarding} graphHasErrors={hasErrors} onSubmit={async ({
          versionDescription: versionDescription_0,
          skipValidation: skipValidation_0
        }) => {
          try {
            setDeployLoading(true);
            await onSubmitAndDeploy({
              action: 'draft',
              versionDescription: versionDescription_0,
              skipValidation: skipValidation_0
            });
            setDeployDialogOpen(false);
          } finally {
            setDeployLoading(false);
          }
        }} />
        </div>
      </Dialog>
    </>;
};
export default PublishActions;